import {
  ApiRef,
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@mainstage/core-plugin-api';
import { ResponseError } from '@mainstage/errors';
import { z } from 'zod';

import {
  type McpToolReference,
  mcpToolReferenceZod,
  paginatedResponseZod,
  PaginationRequest,
  paginationZod,
  toQueryString,
  wittyAgentIdZod,
  wittyUuidZod,
} from './schema';

export const wittyAgentZod = z.object({
  id: wittyAgentIdZod,
  platform_key: z.string().nullable(),
  name: z.string(),
  label: z.string(),
  description: z.string(),
  instructions: z.string(),
  enabled: z.boolean(),
  is_default: z.boolean(),
  is_orchestrator: z.boolean(),
  is_platform_provided: z.boolean(),
  display_order: z.number().int(),
  mcp_tools: z.array(mcpToolReferenceZod),
  mcp_servers: z.array(z.string().min(1)),
  skill_ids: z.array(wittyUuidZod),
  subagent_ids: z.array(wittyAgentIdZod),
  created_at: z.string(),
  updated_at: z.string(),
});

export type WittyAgent = z.infer<typeof wittyAgentZod>;

export const wittyAgentSummaryZod = wittyAgentZod
  .omit({
    instructions: true,
    mcp_tools: true,
    mcp_servers: true,
    skill_ids: true,
    subagent_ids: true,
  })
  .extend({
    skill_count: z.number().int().nonnegative(),
    tool_count: z.number().int().nonnegative(),
  });

export type WittyAgentSummary = z.infer<typeof wittyAgentSummaryZod>;

export type WittyAgentListRequest = PaginationRequest & {
  enabled?: boolean;
  is_platform_provided?: boolean;
  sort_by?:
    | 'label'
    | 'description'
    | 'enabled'
    | 'type'
    | 'skill_count'
    | 'tool_count';
  sort_order?: 'asc' | 'desc';
};

export const wittyAgentCreateZod = z.object({
  name: z.string().min(1).max(255),
  label: z.string().min(1).max(255),
  description: z.string().optional(),
  instructions: z.string().optional(),
  enabled: z.boolean().optional(),
  is_default: z.boolean().optional(),
  display_order: z.number().int().optional(),
  mcp_tools: z.array(mcpToolReferenceZod).optional(),
  mcp_servers: z.array(z.string().min(1)).optional(),
  skill_ids: z.array(wittyUuidZod).optional(),
  subagent_ids: z.array(wittyAgentIdZod).optional(),
});

export type WittyAgentCreate = z.infer<typeof wittyAgentCreateZod>;
export type WittyAgentUpdate = Partial<
  Omit<WittyAgentCreate, 'mcp_tools' | 'mcp_servers' | 'skill_ids'>
>;
export type { McpToolReference } from './schema';
export { mcpToolReferenceToId } from './schema';

export const wittyMcpToolZod = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  server: z.string(),
});

export type WittyMcpTool = z.infer<typeof wittyMcpToolZod>;

export type WittyMcpToolListRequest = PaginationRequest & {
  server?: string[];
};

const capabilityChildZod = z.object({
  id: z.string(),
  display_name: z.string(),
  description: z.string(),
  kind: z.enum(['tool', 'mcp_server']),
  server: z.string().nullable(),
  inherited: z.boolean(),
});

export const wittyAgentCapabilityZod = z.object({
  id: z.string(),
  display_name: z.string(),
  description: z.string(),
  kind: z.enum(['skill', 'tool']),
  server: z.string().nullable(),
  name: z.string().nullable(),
  inherited: z.boolean(),
  children: z.array(capabilityChildZod),
});

const agentCapabilitiesResponseZod = z.object({
  data: z.array(wittyAgentCapabilityZod),
  meta: z.object({
    pagination: paginationZod,
    summary: z.object({
      skill_count: z.number().int().nonnegative(),
      direct_tool_count: z.number().int().nonnegative(),
      effective_tool_count: z.number().int().nonnegative(),
      inherited_tool_count: z.number().int().nonnegative(),
      mcp_server_count: z.number().int().nonnegative(),
    }),
    mcp_servers: z.array(
      z.object({
        server: z.string(),
        tool_count: z.number().int().nonnegative(),
      }),
    ),
  }),
});

export type WittyAgentCapabilities = z.infer<
  typeof agentCapabilitiesResponseZod
>;

export type WittyAgentCapabilitiesRequest = PaginationRequest & {
  type?: Array<'skill' | 'tool'>;
  sort_order?: 'asc' | 'desc';
};

export type WittyConfigurationApi = {
  listAgents(
    request?: WittyAgentListRequest,
  ): Promise<
    z.infer<
      ReturnType<typeof paginatedResponseZod<typeof wittyAgentSummaryZod>>
    >
  >;
  getAgent(id: string): Promise<WittyAgent>;
  listMcpTools(
    request?: WittyMcpToolListRequest,
  ): Promise<
    z.infer<ReturnType<typeof paginatedResponseZod<typeof wittyMcpToolZod>>>
  >;
  lookupMcpTools(ids: string[]): Promise<WittyMcpTool[]>;
  listAgentCapabilities(
    id: string,
    request?: WittyAgentCapabilitiesRequest,
  ): Promise<WittyAgentCapabilities>;
  createAgent(data: WittyAgentCreate): Promise<WittyAgent>;
  updateAgent(id: string, data: WittyAgentUpdate): Promise<WittyAgent>;
  addAgentSkills(id: string, skillIds: string[]): Promise<void>;
  removeAgentSkill(id: string, skillId: string): Promise<void>;
  addAgentMcpTools(id: string, tools: McpToolReference[]): Promise<void>;
  removeAgentMcpTool(id: string, tool: McpToolReference): Promise<void>;
  addAgentMcpServers(id: string, servers: string[]): Promise<void>;
  removeAgentMcpServer(id: string, server: string): Promise<void>;
  deleteAgent(id: string): Promise<void>;
};

export const wittyConfigurationApiRef: ApiRef<WittyConfigurationApi> =
  createApiRef<WittyConfigurationApi>({
    id: 'plugin.witty-configuration',
  });

export class WittyConfigurationClient implements WittyConfigurationApi {
  constructor(
    private readonly fetchApi: FetchApi,
    private readonly discoveryApi: DiscoveryApi,
  ) {}

  private async request<T>(
    path: string,
    schema: z.ZodType<T>,
    options?: RequestInit,
  ): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('witty');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...options,
      headers: options?.body
        ? { 'Content-Type': 'application/json' }
        : undefined,
    });
    if (!response.ok) throw await ResponseError.fromResponse(response);
    const parsed = schema.safeParse(await response.json());
    if (!parsed.success) {
      throw new Error(
        `Invalid Witty configuration response: ${parsed.error.message}`,
      );
    }
    return parsed.data;
  }

  private async mutate(path: string, options: RequestInit): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('witty');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...options,
      headers: options.body
        ? { 'Content-Type': 'application/json' }
        : undefined,
    });
    if (!response.ok) throw await ResponseError.fromResponse(response);
  }

  listAgents(request: WittyAgentListRequest = {}) {
    return this.request(
      `/v1/agents${toQueryString({
        limit: request.limit,
        offset: request.offset,
        search: request.search,
        enabled: request.enabled,
        is_platform_provided: request.is_platform_provided,
        'sort-by': request.sort_by,
        'sort-order': request.sort_order,
      })}`,
      paginatedResponseZod(wittyAgentSummaryZod),
    );
  }

  getAgent(id: string) {
    return this.request(`/v1/agents/${encodeURIComponent(id)}`, wittyAgentZod);
  }

  listMcpTools(request: WittyMcpToolListRequest = {}) {
    return this.request(
      `/v1/mcp-tools${toQueryString({
        limit: request.limit,
        offset: request.offset,
        search: request.search,
        server: request.server,
      })}`,
      paginatedResponseZod(wittyMcpToolZod),
    );
  }

  lookupMcpTools(ids: string[]) {
    return this.request('/v1/mcp-tools/lookup', z.array(wittyMcpToolZod), {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  }

  listAgentCapabilities(
    id: string,
    request: WittyAgentCapabilitiesRequest = {},
  ) {
    return this.request(
      `/v1/agents/${encodeURIComponent(id)}/capabilities${toQueryString({
        limit: request.limit,
        offset: request.offset,
        search: request.search,
        type: request.type,
        'sort-order': request.sort_order,
      })}`,
      agentCapabilitiesResponseZod,
    );
  }

  createAgent(data: WittyAgentCreate) {
    return this.request('/v1/agents', wittyAgentZod, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateAgent(id: string, data: WittyAgentUpdate) {
    return this.request(`/v1/agents/${encodeURIComponent(id)}`, wittyAgentZod, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  addAgentSkills(id: string, skillIds: string[]) {
    return this.mutate(`/v1/agents/${encodeURIComponent(id)}/skills`, {
      method: 'POST',
      body: JSON.stringify({ skill_ids: skillIds }),
    });
  }

  removeAgentSkill(id: string, skillId: string) {
    return this.mutate(
      `/v1/agents/${encodeURIComponent(id)}/skills/${encodeURIComponent(
        skillId,
      )}`,
      { method: 'DELETE' },
    );
  }

  addAgentMcpTools(id: string, tools: McpToolReference[]) {
    return this.mutate(`/v1/agents/${encodeURIComponent(id)}/mcp-tools`, {
      method: 'POST',
      body: JSON.stringify({ mcp_tools: tools }),
    });
  }

  removeAgentMcpTool(id: string, tool: McpToolReference) {
    return this.mutate(
      `/v1/agents/${encodeURIComponent(id)}/mcp-tools${toQueryString({
        server: tool.server,
        name: tool.name,
      })}`,
      { method: 'DELETE' },
    );
  }

  addAgentMcpServers(id: string, servers: string[]) {
    return this.mutate(`/v1/agents/${encodeURIComponent(id)}/mcp-servers`, {
      method: 'POST',
      body: JSON.stringify({ mcp_servers: servers }),
    });
  }

  removeAgentMcpServer(id: string, server: string) {
    return this.mutate(
      `/v1/agents/${encodeURIComponent(id)}/mcp-servers/${encodeURIComponent(
        server,
      )}`,
      { method: 'DELETE' },
    );
  }

  deleteAgent(id: string) {
    return this.mutate(`/v1/agents/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }
}
